import StandardButton from './StandardButton';
import React from 'react';
import { OrderingHooks, OrderingSelectors } from 'polygon-ordering';
import { useAppSelector } from '../app/hooks';
import calcMinBalanceStaged from '../libs/polygon-ordering/src/utils/ordering/calcMinBalanceStaged';
import calcStagedPurchasesInCart from '../libs/polygon-ordering/src/utils/ordering/calcStagedPurchasesInCart';
import calculateStockBalanceData from '../libs/polygon-ordering/src/utils/ordering/calculateStockBalanceData';
import combineStyles from '../utils/combineStyles';
import getThemeLookup from '../selectors/getThemeLookup';
import { CONTAINER_PROPERTIES } from '../utils/theme';
import type { STOCK_BALANCE_THRESHOLDS } from '../libs/polygon-ordering/src/constants';

const { useFormattedCurrency } = OrderingHooks;
const {
  getOpenPurchase,
  getItemAvailability,
  getStockBalances,
  getStagedPurchases,
  getStockBalanceThresholds,
} = OrderingSelectors;

const AddToCartButton = ({
  onClick,
  label,
  value,
  valid,
  showLabelOnly = false,
}: {
  label: string;
  value: number;
  onClick: () => void;
  valid: boolean;
  showLabelOnly?: boolean;
}) => {
  const price = useFormattedCurrency({ cents: value });
  const priceString = `${label} - ${price}${valid ? '' : '*'} `;
  const openPurchase = useAppSelector(getOpenPurchase);
  const p = useAppSelector(getThemeLookup);

  const { quantity, choicesWithQuantity, item, plucode, mimCategoryId } =
    openPurchase as PurchaseWithTotals;
  const [itemAvailability] = useAppSelector(getItemAvailability(item, mimCategoryId));
  const stockBalances = useAppSelector(getStockBalances);
  const stagedPurchases = useAppSelector(getStagedPurchases);
  const stockBalanceThresholds = useAppSelector(getStockBalanceThresholds);
  const quantityMap = calcStagedPurchasesInCart({ purchaseID: openPurchase!.id, stagedPurchases });
  const minBalanceStaged = calcMinBalanceStaged({
    choices: choicesWithQuantity,
    pluQtyMap: quantityMap,
    stockBalances,
    stagedPurchases,
    stockBalanceThresholds,
  });
  const stockBalanceData = calculateStockBalanceData({
    itemID: plucode,
    stockBalances,
    stagedPurchases,
    stockBalanceThresholds,
  });

  const getMaxAllowedQuantity = (
    minBalanceStaged: number | undefined,
    stockBalanceData: {
      itemBalance: any;
      cartAdjustedBalance: any;
      stockBalanceThreshold?: STOCK_BALANCE_THRESHOLDS | null;
      soldOut?: boolean | null;
      soldOutByCartSelections?: boolean | null;
    },
  ) => {
    if (minBalanceStaged) {
      return minBalanceStaged;
    }

    if (stockBalanceData.cartAdjustedBalance) {
      return stockBalanceData.cartAdjustedBalance;
    }

    return stockBalanceData.itemBalance;
  };

  const validateCart = () => {
    const maxQuantity = getMaxAllowedQuantity(minBalanceStaged, stockBalanceData);
    return !valid || quantity > maxQuantity || minBalanceStaged === 0 || !itemAvailability;
  };

  return (
    <StandardButton
      themeKey="addToOrderButton"
      label={showLabelOnly ? label : priceString}
      onClick={onClick}
      disabled={validateCart()}
      style={combineStyles(styles.button, p('modalProceedButton', CONTAINER_PROPERTIES))}
    />
  );
};

export default AddToCartButton;

const styles = {
  button: {
    borderRightWidth: 0,
    borderLeftWidth: 0,
    borderBottomWidth: 0,
    flexGrow: 1,
    padding: 10,
  },
};
